import { BreadcrumbsData, PrimaryButton } from '@systemeio/ui'
import { CoursesLoco } from 'modules/courses/constants/courses-loco'
import { createModule } from 'modules/courses/courses/curriculum/api/module-api'
import CourseCurriculumEmptyList from 'modules/courses/courses/curriculum/components/course-curriculum-empty-list'
import CourseCurriculumSkeleton from 'modules/courses/courses/curriculum/components/course-curriculum-skeleton'
import CourseCurriculumTable from 'modules/courses/courses/curriculum/components/course-curriculum-table'
import CreateModuleModal from 'modules/courses/courses/curriculum/components/create-module-modal'
import CreateStudentModal from 'modules/courses/courses/curriculum/components/create-student-modal'
import { useClassicCourseCurriculum } from 'modules/courses/courses/curriculum/hooks/use-classic-course-curriculum'
import { CreateModuleInterface } from 'modules/courses/courses/curriculum/types/module-interface'
import { useCourse } from 'modules/courses/courses/hooks/use-course'
import { CourseId } from 'modules/courses/courses/types/course-interface'
import { NextPage } from 'next'
import { useRouter } from 'next/router'
import BreadcrumbsDashboard from 'shared/components/breadcrumbs'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'
import { EyeIcon, SettingsIcon, StudentIcon } from '@systemeio/ui/icons'

const Curriculum: NextPage = () => {
  const { t } = useLocoTranslation()
  const router = useRouter()
  const courseId = Number(router.query.courseId) as CourseId

  const {
    data: courseCurriculumData,
    mutate,
    revalidateCourseCurriculum,
  } = useClassicCourseCurriculum(courseId)
  const { courseData } = useCourse(courseId)

  const breadcrumbsData: BreadcrumbsData[] = [
    { caption: t('dashboard.courses.title_plural'), href: '/courses', isStatic: true },
    { caption: courseData?.name || '' },
  ]

  const createModuleWithUpdate = async (courseId: CourseId, module: CreateModuleInterface) => {
    const newModule = (await createModule(courseId, module)).data
    await mutate(data => {
      if (data) {
        return {
          ...data,
          modules: [...data.modules, newModule],
        }
      }
    }, false)
    await revalidateCourseCurriculum()
  }

  return (
    <div className="flex flex-col">
      <div className="flex flex-col items-center justify-between gap-4 md:flex-row lg:gap-5">
        <BreadcrumbsDashboard data={breadcrumbsData} isFetching={!courseData} />
        <div className="flex flex-wrap gap-2.5 md:flex-nowrap lg:gap-4">
          <CreateStudentModal
            courseCurriculumData={courseCurriculumData}
            courseId={courseId}
            disabledButton={courseData?.isDeactivated}
          />
          <PrimaryButton
            as={LinkWithoutPrefetch}
            href={`/courses/students?filter=%7B%22course%22%3A${courseId}%7D`}
            aria-disabled={!courseCurriculumData}
          >
            <StudentIcon />
            <span className="hidden xl:block">{t('dashboard.students.title_plural')}</span>
          </PrimaryButton>
          <PrimaryButton
            as={LinkWithoutPrefetch}
            href={courseCurriculumData?.loginLinkUrl}
            target={'_blank'}
            aria-disabled={!courseCurriculumData}
          >
            <EyeIcon fill="rgb(var(--background-secondary))" />
            <span className="hidden xl:block">{t('dashboard.curriculum.preview_course')}</span>
          </PrimaryButton>
          <PrimaryButton
            as={LinkWithoutPrefetch}
            href={`/courses/${courseId}`}
            aria-disabled={!courseCurriculumData}
          >
            <SettingsIcon fill="rgb(var(--background-secondary))" />
            <span className="hidden xl:block">{t('global.settings')}</span>
          </PrimaryButton>
        </div>
      </div>
      {courseCurriculumData ? (
        courseCurriculumData.modules.length > 0 ? (
          <>
            <CourseCurriculumTable
              modules={courseCurriculumData.modules}
              mutate={mutate}
              courseId={courseId}
            />
            <div className="mt-10 self-center">
              <CreateModuleModal
                onModuleCreate={createModuleWithUpdate}
                courseId={courseId}
                disabled={!courseCurriculumData}
              />
            </div>
          </>
        ) : (
          <CourseCurriculumEmptyList
            onModuleCreate={createModuleWithUpdate}
            courseId={courseId}
            disabled={!courseCurriculumData}
          />
        )
      ) : (
        <CourseCurriculumSkeleton />
      )}
    </div>
  )
}

export const getServerSideProps = getBaseServerSideProps(CoursesLoco, 'header.top_menu.courses')

export default Curriculum
